import { Injectable } from '@angular/core';
import { DbContext, DbTable } from '../data/dbcontext.service';
import {
  OnSiteMovementEditor,
  OnSiteMovementPerLegEditor,
  OnSiteMovementStatuses,
  OnSiteMovementTypes
} from '../../models/on-site-movement/on-site-movement.models';
import { DailyLogOperationInstance } from '../../models/projects/operation.models';
import { MetadataService } from '../../services/metadata/metadata-service.service';
import { VesselService } from '../../services/vessel/vessel.service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { TimeZoneService } from '../../services/time-zone/time-zone.service';
import { DateService } from '../../services/date/date.service';
import { State } from '@progress/kendo-data-query';
import { ErrorType, ValidationResult } from '../../models/projects/validation.models';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { AppConfig } from '../../app/app.config';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class OnSiteMovementService {
  constructor(
    private dbContext: DbContext,
    private metadataService: MetadataService,
    private vesselService: VesselService,
    private timezoneService: TimeZoneService,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService,
    private http: HttpClient,
  ) {
    this.onSiteMovementTable = this.dbContext.tables.find(
      t => t.name === 'OnSiteMovement'
    );
  }

  private onSiteMovementTable: DbTable;
  private readonly JackDownOnSiteMovementOperationGuid =
    '6DCF88AE-344B-4B8F-BAFD-458634B38532';
  private readonly JackUpOnSiteMovementOperationGuid =
    'AED38985-7D88-4535-9159-54176ECBDABA';

  private state: State = {
    take: 50,
    skip: 0
  };

  /// Returns default grid state
  getState(): State {
    return this.state;
  }

  /// Remove all On Site Movement records from the local web DB
  public async deleteAllLocalOnSiteMovementRecords() {
    await this.dbContext.removeAll(this.onSiteMovementTable);
  }

  /// Save a single On Site Movement record to the local web DB
  public saveOnSiteMovementRecordLocally(onSiteMovementRecord: OnSiteMovementEditor) {
    this.dbContext.addOrUpdate(
      this.onSiteMovementTable,
      onSiteMovementRecord.id,
      onSiteMovementRecord
    );
  }

  /// Save a collection of On Site Movement records to the local web DB
  public async saveOnSiteMovementRecordsLocally(onSiteMovementRecords: OnSiteMovementEditor[]) {
    for (let i = 0; i < onSiteMovementRecords.length; i++) {
      this.saveOnSiteMovementRecordLocally(onSiteMovementRecords[i]);
    }
  }

  async getOnSiteMovementsForDateFromApi(dailyLogDateUnixMs: number): Promise<OnSiteMovementEditor[]> {
    return this.http
      .get<OnSiteMovementEditor[]>(
        `${AppConfig.settings.apiUrl}api/jacking-v2/on-site-movement/${dailyLogDateUnixMs}/all`
      )
      .toPromise()
      .then(async (data: OnSiteMovementEditor[]) => {
        // date object conversion
        data = await this.dateParserService.deserializeDates(data, true, this.timeZoneService);

        // custom date show logic for each record
        for (const record of data) {
          await this.deserializeOnSiteMovementDates(record);
          this.ensureLegShells(record);
        }

        return data;
      });
  }

  public async getOnSiteMovementRecordsForDay(date: PtLuxonDate, endOfDay: PtLuxonDate): Promise<OnSiteMovementEditor[]> {
    // Get all records from Web DB
    let onSiteMovementRecords = (await this.dbContext.getAll(this.onSiteMovementTable)) as OnSiteMovementEditor[];

    // Load local properties (+ initialize dates)
    for (const record of onSiteMovementRecords) {
      await this.deserializeOnSiteMovementDates(record);
      this.ensureLegShells(record);
    }

    onSiteMovementRecords = onSiteMovementRecords.filter(a =>
      (a.startDate?.dateTime >= date.dateTime && a.startDate?.dateTime < endOfDay.dateTime) ||
      (a.endDate?.dateTime >= date.dateTime && a.endDate?.dateTime < endOfDay.dateTime) ||
      (date.dateTime >= a.startDate?.dateTime && date.dateTime <= a.endDate?.dateTime)
    );

    return onSiteMovementRecords;
  }

  /// Ensure legs array exists and has shells for each leg number we know about.
  /// The API should already return ordered legs; this is mainly for locally-created records.
  private ensureLegShells(record: OnSiteMovementEditor, defaultLegCount: number = 4) {
    if (!record) {
      return;
    }

    if (!record.legs) {
      record.legs = [];
    }

    // If API returned legs, trust that count/order. Otherwise fall back to defaultLegCount.
    const maxLeg = record.legs.reduce((m, l) => Math.max(m, l?.legNumber || 0), 0);
    const legCount = maxLeg > 0 ? maxLeg : defaultLegCount;

    for (let legNo = 1; legNo <= legCount; legNo++) {
      if (!record.legs.some(l => l.legNumber === legNo)) {
        record.legs.push({
          id: 0,
          legNumber: legNo,
          load: null,
          ext: null,
          displayOrder: null
        } as any);
      }
    }

    // Keep stable ordering: displayOrder first, then legNumber
    record.legs = record.legs.slice().sort((a: any, b: any) =>
      ((a.displayOrder ?? a.legNumber) - (b.displayOrder ?? b.legNumber))
    );
  }

  /// Initialize PT Date Objects and Set Local Date/Time display properties
  private async deserializeOnSiteMovementDates(record: OnSiteMovementEditor) {
    if (record.startDate && record.startDate.dateTime) {
      record.startDate = await this.dateParserService.deserializePtLuxonDate(record.startDate, this.timeZoneService);
    }

    if (record.endDate && record.endDate.dateTime) {
      record.endDate = await this.dateParserService.deserializePtLuxonDate(record.endDate, this.timeZoneService);
    }

    if (record.lastModifiedDate && record.lastModifiedDate.dateTime) {
      record.lastModifiedDate = await this.dateParserService.deserializePtLuxonDate(record.lastModifiedDate, this.timeZoneService);
    }

    // local day values for timezone
    record.startDay = record.startDate ? record.startDate.getDay() : null;
    record.endDay = record.endDate && record.endDate.dateTime ? record.endDate.getDay() : null;

    // offsets (prefer explicit offsets if present, otherwise compute)
    record.startOffset = record.startDate && record.startDate.dateTime
      ? await this.timezoneService.offsetForTime(record.startDate.getDate())
      : 0;

    record.endOffset = record.endDate && record.endDate.dateTime
      ? await this.timezoneService.offsetForTime(record.endDate.getDate())
      : 0;

    // display strings
    record.startDateAsDateTimeString = record.startDate?.dateTime
      ? this.dateService.setFullTimeString(record.startDate.dateTime)
      : '';

    record.endDateAsDateTimeString = record.endDate?.dateTime
      ? this.dateService.setFullTimeString(record.endDate.dateTime)
      : '';

    record.lastModifiedDateAsDateTimeString = record.lastModifiedDate?.dateTime
      ? this.dateService.setFullTimeString(record.lastModifiedDate.getDate())
      : '';

    record.startDateAsTimeString = record.startDate?.dateTime
      ? this.dateService.setTimeStringFromPtLuxonDate(record.startDate)
      : '';

    record.endDateAsTimeString = record.endDate?.dateTime
      ? this.dateService.setTimeStringFromPtLuxonDate(record.endDate)
      : '';
  }

  private operationIsJackUp(operationInstance: DailyLogOperationInstance): boolean {
    return (
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackUpOnSiteMovementOperationGuid.toLowerCase()
    );
  }

  private operationIsJackDown(operationInstance: DailyLogOperationInstance): boolean {
    return (
      operationInstance.projectSpecificOperation.operationSummary.guid.toLowerCase() ===
      this.JackDownOnSiteMovementOperationGuid.toLowerCase()
    );
  }

  public operationRequiresOnSiteMovementRecord(operationInstance: DailyLogOperationInstance): boolean {
    return (
      this.operationIsJackDown(operationInstance) ||
      this.operationIsJackUp(operationInstance)
    );
  }

  public async createOnSiteMovementRecordForOperation(
    operationInstance: DailyLogOperationInstance,
    locationId: number
  ): Promise<OnSiteMovementEditor> {
    if (!operationInstance) {
      return undefined;
    }

    if (!this.operationRequiresOnSiteMovementRecord(operationInstance)) {
      return undefined;
    }

    const onSiteMovementRecord = new OnSiteMovementEditor();

    onSiteMovementRecord.startDate = operationInstance.startTime;
    onSiteMovementRecord.endDate = operationInstance.endTime;

    onSiteMovementRecord.startOffset = operationInstance.startTime?.dateTime
      ? operationInstance.startTime.dateTime.offset / 60
      : 0;

    onSiteMovementRecord.endOffset = operationInstance.endTime && operationInstance.endTime.dateTime
      ? operationInstance.endTime.dateTime.offset / 60
      : 0;

    onSiteMovementRecord.operationId = operationInstance.id;
    onSiteMovementRecord.localOperationId = operationInstance.localId;

    onSiteMovementRecord.locationId = locationId;

    const vessel = await this.vesselService.getEditor();
    onSiteMovementRecord.vesselId = vessel ? vessel.id : null;

    await this.setStatus(onSiteMovementRecord, OnSiteMovementStatuses.NotStarted);

    if (this.operationIsJackUp(operationInstance)) {
      await this.setType(onSiteMovementRecord, OnSiteMovementTypes.JackUp);
    } else if (this.operationIsJackDown(operationInstance)) {
      await this.setType(onSiteMovementRecord, OnSiteMovementTypes.JackDown);
    }

    // Create shell legs for UI when record is new and has no API-provided legs.
    this.ensureLegShells(onSiteMovementRecord, 4);

    return onSiteMovementRecord;
  }

  public async setStatus(record: OnSiteMovementEditor, status: OnSiteMovementStatuses) {
    const statuses = await this.metadataService.getOnSiteMovementStatus();
    record.status = statuses.find(s => s.tag === status);
  }

  public async setType(record: OnSiteMovementEditor, type: OnSiteMovementTypes) {
    const types = await this.metadataService.getOnSiteMovementTypes();
    record.type = types.find(t => t.tag === type);
  }

  /// Basic validation for On-Site Movement to check if fields have values
  public async validateRecord(record: OnSiteMovementEditor): Promise<ValidationResult> {
    const result = new ValidationResult();

    // Start Date
    if (!record.startDate) {
      result.addError('Start Date must have a value', ErrorType.Error);
    }
    if (!record.startDateConfirmed) {
      result.addError('Start Date must be confirmed', ErrorType.Error);
    }

    // End Date
    if (!record.endDate) {
      result.addError('End Date must have a value', ErrorType.Error);
    }
    if (!record.endDateConfirmed) {
      result.addError('End Date must be confirmed', ErrorType.Error);
    }

    // Legs (Load + Ext)
    const legs = record.legs ?? [];
    if (legs.length === 0) {
      result.addError('Leg values are missing', ErrorType.Error);
    } else {
      for (const leg of legs) {
        if (leg.load === null || leg.load === undefined) {
          result.addError(`Leg ${leg.legNumber} load is blank`, ErrorType.Error);
        }
        if (leg.ext === null || leg.ext === undefined) {
          result.addError(`Leg ${leg.legNumber} ext is blank`, ErrorType.Error);
        }
      }
    }

    // Final Air Gap
    if (record.finalAirGap === null || record.finalAirGap === undefined) {
      result.addError('Final Air Gap is blank', ErrorType.Error);
    }

    // Reason For Jacking
    if (!record.reasonForJacking) {
      result.addError('Reason For Jacking is blank', ErrorType.Error);
    }

    // Jacking Engineer 1/2
    if (record.jackingEngineer1Name === null || record.jackingEngineer1Name === '') {
      result.addError('Jacking Engineer 1 is blank', ErrorType.Error);
    }
    if (record.jackingEngineer2Name === null || record.jackingEngineer2Name === '') {
      result.addError('Jacking Engineer 2 is blank', ErrorType.Error);
    }

    return result;
  }

  public async validateOnSiteMovementForDailyLog(date: PtLuxonDate): Promise<Boolean> {
    const currentDay = await date.getDay();

    let onSiteMovementRecords = (await this.dbContext.getAll(this.onSiteMovementTable)) as OnSiteMovementEditor[];

    for (const record of onSiteMovementRecords) {
      await this.deserializeOnSiteMovementDates(record);
    }

    onSiteMovementRecords = onSiteMovementRecords.filter(o => o.endDay === currentDay);

    return onSiteMovementRecords.every(e => e.status.id === OnSiteMovementStatuses.Complete);
  }
}



// WEBPACK FOOTER //
// ./src/services/on-site-movement/on-site-movement.service.ts